import React, { useEffect, useState } from 'react';
import { superAdminDashboardService } from '../../services/superAdminService';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

interface DashboardStats {
  totalTenants: number;
  activeTenants: number;
  totalAdmins: number;
  totalShops: number;
  totalStaff: number;
}

interface TenantInfo {
  _id: string;
  name: string;
  code: string;
  status: string;
  shopCount: number;
  staffCount: number;
  createdAt: string;
}

const COLORS = ['#667eea', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const SuperAdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentTenants, setRecentTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await superAdminDashboardService.getStats();
      if (response.data?.success) {
        setStats(response.data.data.stats);
        setRecentTenants(response.data.data.recentTenants || []);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const tenantStatusData = [
    { name: 'Active', value: stats?.activeTenants || 0 },
    { name: 'Inactive', value: (stats?.totalTenants || 0) - (stats?.activeTenants || 0) },
  ];

  const tenantResourceData = recentTenants.slice(0, 5).map(t => ({
    name: t.code,
    shops: t.shopCount,
    staff: t.staffCount,
  }));

  const statCards = [
    {
      label: 'Total Tenants',
      value: stats?.totalTenants || 0,
      bgColor: 'bg-primary-500',
      textColor: 'text-white',
    },
    {
      label: 'Active Tenants',
      value: stats?.activeTenants || 0,
      bgColor: 'bg-green-500',
      textColor: 'text-white',
    },
    {
      label: 'Total Admins',
      value: stats?.totalAdmins || 0,
      bgColor: 'bg-amber-500',
      textColor: 'text-white',
    },
    {
      label: 'Total Shops',
      value: stats?.totalShops || 0,
      bgColor: 'bg-red-500',
      textColor: 'text-white',
    },
    {
      label: 'Total Staff',
      value: stats?.totalStaff || 0,
      bgColor: 'bg-purple-500',
      textColor: 'text-white',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-primary-500 mb-2">Super Admin Dashboard</h1>
        <p className="text-gray-600">Overview of all tenants and system statistics</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {statCards.map((card, index) => (
          <div
            key={index}
            className={`${card.bgColor} ${card.textColor} rounded-xl shadow-lg p-6 transform transition-all duration-200 hover:scale-105 hover:shadow-xl`}
          >
            <p className="text-4xl font-bold mb-2">{card.value}</p>
            <p className="text-sm opacity-90">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Tenant Status Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={tenantStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }: { name?: string; percent?: number }) =>
                  `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {tenantStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Resources by Tenant (Top 5)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tenantResourceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Bar dataKey="shops" fill="#667eea" name="Shops" radius={[8, 8, 0, 0]} />
              <Bar dataKey="staff" fill="#10b981" name="Staff" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Tenants Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Recent Tenants</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Shops</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Staff</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentTenants.map((tenant) => (
                <tr key={tenant._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <strong className="text-sm font-semibold text-gray-900">{tenant.code}</strong>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                      tenant.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {tenant.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.shopCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.staffCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {recentTenants.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No tenants found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
